import {
	hamburgerButton,
	overlay,
	menuPrimary,
	navPrimary,
	logo,
	courseSelect,
	adminPostType,
	typeOfCourse,
	isCompletable,
	expandTranscriptionInside,
	expandTranscriptionOutside,
	transcriptionBox,
	audioBox,
	popups,
	searchPopup,
	focusTrapSearchBox,
	searchRestrictions,
	searchSubmit,
	sidebar,
	viewport,
	lessonSidebar,
	excludeSearchToggle,
	postTypesFacet,
	searchForm,
	searchTerms,
	searchToggle,
	searchMenuItem,
	header,
	menuParentItems,
	mqMenu,
	registerUserRoles,
	registerForm,
	wistiaEmbed,
	earningsBar,
	surveySubmitButton,
	countdownBar,
	countdownContainer,
} from './elements.js'
import { capitalizeString, expandSection, collapseSection } from './lib.js'
import { isAnyCheckboxChecked, simulateClick } from './utils.js'
import {
	disableBodyScroll,
	enableBodyScroll,
} from '../../../node_modules/body-scroll-lock/lib/bodyScrollLock.es6.js'

/**
 * Toggle main hamburger menu
 *
 * @version     1.0.0
 */
export function toggleMenu(e) {
	if (hamburgerButton && menuPrimary) {
		const expanded =
			hamburgerButton.getAttribute('aria-expanded') === 'true' || false
		const menuState =
			menuPrimary.getAttribute('data-visible') === 'true' || false

		if (
			e.currentTarget === hamburgerButton ||
			((e.currentTarget === overlay || e.keyCode === 27) && expanded)
		) {
			menuPrimary.setAttribute('data-visible', !menuState)
			navPrimary.setAttribute('data-visible', !menuState)
			logo.setAttribute('data-visible', menuState)
			header.setAttribute('data-visible', menuState)
			searchToggle.setAttribute('data-visible', menuState)
			overlay.setAttribute('data-visible', !menuState)
			hamburgerButton.setAttribute('aria-expanded', !expanded)
		}
	}
}

/**
 * Handle card click to make the whole element clickable allowing inside links
 *
 * @version     1.0.0
 */
export function handleCardClick(e) {
	const isTextSelected = window.getSelection().toString()
	const cardLink = e.target.closest('.js-card').querySelector('.js-main-link')
	const clickableElements = e.target.closest('.js-card').querySelectorAll('a')
	clickableElements.forEach((el) =>
		el.addEventListener('click', (ev) => ev.stopPropagation())
	)
	if (!isTextSelected) {
		cardLink.click()
	}
}

/**
 * Autoselect current course
 *
 * @version     1.2.0
 */
export function selectCourseOnLoad() {
	const postType = adminPostType.value
	let course

	if (postType.includes('bf')) {
		course = postType.toUpperCase()
	} else if (postType.includes('sanvalentin')) {
		course = 'San Valentín'
	} else if (postType.includes('padre')) {
		course = 'Día del Padre'
	} else if (postType.includes('madre')) {
		course = 'Día de la Madre'
	} else {
		course = capitalizeString(postType)
	}
	courseSelect.value = course
}

/**
 * Autoselect current course
 *
 * @version     1.1.0
 */
export function toggleCourseCompletable() {
	for (let i = 0; i < typeOfCourse.length; i++) {
		typeOfCourse[i].addEventListener('change', function () {
			const type = this.value
			if (type === 'module' || type === 'help' || type === 'bonus') {
				isCompletable.checked = false
			} else {
				isCompletable.checked = true
			}
			if (excludeSearchToggle) {
				if (type !== 'lesson') {
					excludeSearchToggle.checked = true
				} else {
					excludeSearchToggle.checked = false
				}
			}
		})
	}
}

/**
 * Apply comment styling after voting
 *
 * @version     1.0.0
 */
export function applyCommentVoteStyling(e) {
	const likeButton = e.currentTarget
	const counter = likeButton.nextElementSibling
	MutationObserver = window.MutationObserver || window.WebKitMutationObserver

	const observer = new MutationObserver((mutations, observer) => {
		//  Fired when a mutation occurs
		likeButton.parentElement.setAttribute('data-voted', 'true')
	})

	observer.observe(counter, {
		characterData: true,
		childList: true,
	})
}

/**
 * Expand/Collapse transcription box
 *
 * @version     1.0.0
 */
export function toggleTranscription(e) {
	const isCollapsed =
		transcriptionBox.getAttribute('data-collapsed') === 'true' || false

	if (isCollapsed) {
		expandTranscriptionOutside.textContent = 'Contraer transcripción'
		expandSection(transcriptionBox)
	} else {
		expandTranscriptionOutside.textContent = 'Expandir transcripción'
		collapseSection(transcriptionBox, false, 240)
	}
	transcriptionBox.setAttribute('data-collapsed', !isCollapsed)
	expandTranscriptionInside.setAttribute('aria-hidden', isCollapsed)
}

/**
 * Expand/Collapse audio box
 *
 * @version     1.0.0
 */
export function toggleAudio(e) {
	const isCollapsed =
		audioBox.getAttribute('data-collapsed') === 'true' || false

	if (isCollapsed) {
		expandSection(audioBox)
	} else {
		collapseSection(audioBox)
	}
	audioBox.setAttribute('data-collapsed', !isCollapsed)
}

/**
 * Show/Hide question popups on ESC key press
 *
 * @version     1.0.0
 */
export function closeAllQuestionPopups(e) {
	if (e.keyCode === 27) {
		popups.forEach((item) => {
			item.setAttribute('aria-hidden', 'true')
		})
	}
}

/**
 * Show/Hide question popups on overlay click
 *
 * @version     1.0.0
 */
export function closePopupOnOverlayClick(e) {
	if (!e.currentTarget.querySelector('.js-popup-wrapper').contains(e.target)) {
		e.currentTarget.setAttribute('aria-hidden', 'true')
	}
}

/**
 * Toggle search box
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function toggleSearchBox(e) {
	const isVisible = searchPopup.getAttribute('data-visible') === 'true' || false
	if (!isVisible) {
		if (e.target.closest('.js-search-toggle') && !e.keyCode) {
			searchPopup.setAttribute('data-visible', !isVisible)
			setTimeout(() => {
				focusTrapSearchBox.activate()
			}, 150)
			disableBodyScroll(searchPopup)
		}
	} else if (
		(e.target.closest('.js-search-toggle') && !e.keyCode) ||
		e.target === searchPopup ||
		e.keyCode === 27
	) {
		searchPopup.setAttribute('data-visible', !isVisible)
		focusTrapSearchBox.deactivate()
		enableBodyScroll(searchPopup)
	}
}

/**
 * Toggle search status if no post type is checked and enable/disable associated Q&A post types
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function toggleSearchStatus(e) {
	if (isAnyCheckboxChecked(searchRestrictions)) {
		searchSubmit.removeAttribute('disabled')
	} else {
		searchSubmit.setAttribute('disabled', '')
	}
}

/**
 * Make sidebar sticky only if there is enough vertical space
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function makeSidebarSticky() {
	const spacing = 88 // sidebar top & bottom spacing
	if (sidebar) {
		if (sidebar.clientHeight + spacing * 2 < viewport.clientHeight) {
			sidebar.classList.add('c-sidebar--sticky')
		} else {
			sidebar.classList.remove('c-sidebar--sticky')
		}
	}

	if (lessonSidebar) {
		if (lessonSidebar.clientHeight + spacing * 2 < viewport.clientHeight) {
			lessonSidebar.classList.add('c-lesson__sidebar--sticky')
		} else {
			lessonSidebar.classList.remove('c-lesson__sidebar--sticky')
		}
	}
}

/**
 * Toggle popup
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function togglePopup(e) {
	e.preventDefault()
	const target = e.currentTarget.getAttribute('data-target')
	const popup = document.querySelector(`.js-popup[data-popup="${target}"]`)
	const state = popup.getAttribute('aria-hidden') === 'true' || false
	popup.setAttribute('aria-hidden', !state)
}

/**
 * Append categories to search facet
 *
 * @version     1.1.0
 * @since       2021.1
 */
export function appendFacetCategories() {
	/* eslint-disable */
	(function ($) {
		$(window).on('facetwp-loaded load', function () {
			const programs = ['genesis', 'upscale', 'polaris', 'masteries', 'nova'];
			const sidebarTitles = document.querySelector('.js-sidebar-search-subtitle')
			if( ! sidebarTitles ){
				programs.forEach(program => {
					
					const course = postTypesFacet.querySelector(
						`.facetwp-checkbox[data-value="${program}"]`
					)
						
					const qa = postTypesFacet.querySelector(
						`.facetwp-checkbox[data-value="qa-${program}"]`
					)
				
					let content = ''
	
					const wrapper = document.createElement('h3')
					wrapper.className = `[ c-sidebar__subtitle c-sidebar__subtitle--${program} ][ js-sidebar-search-subtitle ]`
					if('genesis' === program) {
						 content = document.createTextNode('Genesis™')
					} else if ('upscale' === program) {
						 content = document.createTextNode('Upscale Ecommerce™')					
					} else if ('polaris' === program){
						 content = document.createTextNode('Polaris Mastermind™')
					} else if ('masteries' === program){
						content = document.createTextNode('Masteries')
					} else if ('nova' === program){
						content = document.createTextNode('Nova')
				  }
					wrapper.appendChild(content);
					if (course) {
						postTypesFacet.insertBefore(wrapper, course)
					} else if (qa) {
						postTypesFacet.insertBefore(wrapper, qa)
					}
				});
			}
			const facetSearch = document.querySelector('input.facetwp-search')
			searchTerms.textContent = `"${facetSearch.value}"`
			makeSidebarSticky()
		})
	})(jQuery)
	/* eslint-enable */
}

/**
 * Toggle action items help video popup
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function joinCheckboxesOnSubmit(e) {
	e.preventDefault()
	const hiddenInput = searchForm.querySelector('[name="_programs"]')
	const checked = []
	for (let i = 0; searchRestrictions[i]; ++i) {
		if (searchRestrictions[i].checked) {
			checked.push(searchRestrictions[i].value)
		}
	}
	const checkdString = `${checked.join()},masteries`
	hiddenInput.value = checkdString
	searchForm.submit()
}

/**
 * Move search button on mobile
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function moveSearchButton(e) {
	if (e.matches) {
		searchMenuItem.appendChild(searchToggle)
	} else {
		header.appendChild(searchToggle)
	}
}

/**
 * Handle main menu clicks to show submenus
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function handleMenuClick(e) {
	if (e.target.hasAttribute('aria-expanded')) {
		e.preventDefault()
		const menuItem = e.target.closest('[aria-expanded]')
		const isExpanded =
			menuItem.getAttribute('aria-expanded') === 'true' || false
		const subMenu = menuItem.nextElementSibling

		if (!mqMenu.matches) {
			if (isExpanded) {
				collapseSection(subMenu)
			} else {
				menuParentItems.forEach((item) => {
					if (
						item !== menuItem &&
						item.getAttribute('aria-expanded') === 'true'
					) {
						item.setAttribute('aria-expanded', false)
						collapseSection(item.nextElementSibling)
					} else {
						expandSection(subMenu)
					}
				})
			}
		} else if (!isExpanded) {
			menuParentItems.forEach((item) => {
				if (
					item !== menuItem &&
					item.getAttribute('aria-expanded') === 'true'
				) {
					item.setAttribute('aria-expanded', false)
					item.nextElementSibling.setAttribute('data-collapsed', true)
				}
			})
		}
		menuItem.setAttribute('aria-expanded', !isExpanded)
		subMenu.setAttribute('data-collapsed', isExpanded)
	}
}

/**
 * Move search button on mobile
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function toggleSidebar(e) {
	const sidebarContent = e.currentTarget.nextElementSibling
	const isExpanded =
		e.currentTarget.getAttribute('aria-expanded') === 'true' || false

	if (isExpanded) {
		collapseSection(sidebarContent)
	} else {
		expandSection(sidebarContent)
	}

	sidebarContent.setAttribute('data-collapsed', isExpanded)
	e.currentTarget.setAttribute('aria-expanded', !isExpanded)
}

/**
 * Scroll to firts result on facetWP refresh
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function scrollLoopToTop() {
	if (FWP.loaded) {
		jQuery('html, body').animate(
			{ scrollTop: jQuery('.facetwp-template').offset().top - 128 },
			500
		)
	}
}

/**
 * Jopin user permissions in zapier format
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function joinUserPermissions() {
	const hiddenInput = registerForm.querySelector('.js-role-data input')
	const checked = []
	for (let i = 0; registerUserRoles[i]; ++i) {
		if (registerUserRoles[i].checked) {
			checked.push(`${registerUserRoles[i].value}:add`)
		}
	}
	const checkdString = checked.join(';')
	hiddenInput.value = checkdString
}

/**
 * Mark lesson completed on wistia video completed
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function markCompleteOnWistiaPlayed() {
	const id = wistiaEmbed.getAttribute('data-id')

	window._wq = window._wq || []
	_wq.push({
		id,
		onReady(video) {
			video.bind('end', () => {
				const completeLessonButton = document.querySelector('a.wpc-button')
				if (completeLessonButton.classList.contains('wpc-button-complete')) {
					simulateClick(completeLessonButton)
				}
			})
		},
	})
}

/**
 * Get video aspect ratio
 *
 * @version     1.0.1
 * @since       2022.6
 */
export function calculateWistiaAspectatio() {
	const id = wistiaEmbed.getAttribute('data-id')
	const videoWrapper = wistiaEmbed.querySelector('.js-wistia-container')
	window._wq = window._wq || []
	_wq.push({
		id,
		onReady(video) {
			const ratio = (1 / video.aspect()) * 100
			if (!isNaN(ratio)) {
				const ratioVar = `${ratio}%`
				videoWrapper.style.setProperty('--aspect-ratio', ratioVar)
			}
		},
	})
}

/**
 * Check terms and conditions input on accept click
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function acceptTerms(e) {
	const termsCheckbox = document.getElementById(
		e.currentTarget.getAttribute('data-target')
	)
	const referralButton = document.querySelector(
		`[data-button="${e.currentTarget.getAttribute('data-target')}"]`
	)
	termsCheckbox.checked = 'checked'
	referralButton.classList.remove('c-button--disabled')
}

/**
 * Enable referrarl button when user accepts Terms and conditions
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function enableReferralButton(e) {
	const status = e.currentTarget.checked
	const referralButton = document.querySelector(
		`[data-button="${e.currentTarget.getAttribute('name')}"]`
	)

	if (status) {
		referralButton.classList.remove('c-button--disabled')
	} else {
		referralButton.classList.add('c-button--disabled')
	}
}

/**
 * Move search button on mobile
 *
 * @version     1.0.0
 * @since       2021.1
 */
export function moveEarningsBar(e) {
	if (e.matches) {
		header.insertBefore(earningsBar, navPrimary)
	} else {
		// menuPrimary.appendChild(earningsBar)
		menuPrimary.insertBefore(earningsBar, menuPrimary.firstChild)
		// header.appendChild(searchToggle)
	}
}

/**
 * Move search button on mobile
 *
 * @version     1.0.0
 * @since       2023.26
 */
export function moveCountdownBar(e) {
	if (e.matches) {
		countdownContainer.appendChild(countdownBar)
	} else {
		menuPrimary.appendChild(countdownBar)
	}
}

/**
 * Change form button to indicate loading state
 *
 * @version     1.0.0
 * @since       2022.11
 */
export function indicateLoadingState(e) {
	surveySubmitButton.classList.add('is-loading')
	surveySubmitButton.value = 'Enviando...'
}

/**
 * Export User Completed Lessons
 *
 * @since       2024.5
 */
export async function exportUserCompletedLessons(e) {
	const button = e.currentTarget
	const targetData = button.getAttribute('data-target')

	try {
		button.disabled = true

		const response = await fetch(sqpx_theme_app_object.admin_ajax_url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
			},
			body: new URLSearchParams({
				action: 'export_csv_request',
				export_csv_nonce: sqpx_theme_app_object.export_csv_nonce,
				targetData,
			}),
			credentials: 'same-origin',
		})
		const data = await response.json()
		if (data.success) {
			// Generate a link and download it.
			const link = document.createElement('a')
			link.download = data.data.file_name
			link.href = data.data[0].file_url
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			link.remove()
			button.disabled = false
		} else {
			const err = new Error(data.data.message)
			throw err
		}
	} catch (error) {
		alert(error)
		button.disabled = false
	}
}
